import { useClient } from '@cortex/react-query';
import { providerFailureFromUnknown, type ModelConfig, type ProviderFailure } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type CSSProperties } from 'react';

import { BackButton } from '../../components/common/BackButton';
import { EmptyState } from '../../components/common/EmptyState';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { TestImageModal } from '../../components/models/TestImageModal';
import { routes } from '../../lib/constants';
import {
  applyModelParamFields,
  emptyModelParamFields,
  readModelParamFields,
  type IModelParamFields,
} from '../../lib/model-params';

interface ITestResult {
  success: boolean;
  latencyMs: number;
  timeToFirstTokenMs?: number;
  tokensPerSecond?: number;
  totalTokens?: number;
  preview: string;
  reasoning?: string;
  error?: string;
  failure?: ProviderFailure;
}

export const Route = createFileRoute('/models/$modelId')({ component: ModelDetailPage });

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };
const grid2: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--sp-3)',
};

function ModelDetailPage() {
  const { modelId } = Route.useParams();
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();

  const model = useQuery({
    queryKey: ['model-config', modelId],
    queryFn: () => client.http.get(`v1/model-configs/${modelId}`).json<ModelConfig>(),
  });

  const [testImageOpen, setTestImageOpen] = useState(false);
  const [name, setName] = useState('');
  const [provider, setProvider] = useState<string>('OPENAI');
  const [modelIdField, setModelIdField] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [params, setParams] = useState<IModelParamFields>(emptyModelParamFields);
  const setParam = (key: keyof IModelParamFields, value: string) =>
    setParams((p) => ({ ...p, [key]: value }));

  useEffect(() => {
    if (model.data) {
      setName(model.data.name);
      setProvider(model.data.provider);
      setModelIdField(model.data.modelId);
      setEndpoint(model.data.endpoint ?? '');
      setParams(readModelParamFields(model.data.config));
    }
  }, [model.data]);

  const update = useMutation({
    mutationFn: () =>
      client.http
        .patch(`v1/model-configs/${modelId}`, {
          json: {
            name,
            provider,
            modelId: modelIdField,
            endpoint: endpoint.trim() || undefined,
            ...(apiKey.trim() ? { creds: apiKey.trim() } : {}),
            // Merge params into the existing blob so other stored keys survive.
            config: applyModelParamFields(
              (model.data?.config ?? null) as Record<string, unknown> | null,
              params,
            ),
          },
        })
        .json<ModelConfig>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['model-config', modelId] });
      setApiKey(''); // Clear API key field after successful save
    },
  });

  const remove = useMutation({
    mutationFn: () => client.http.delete(`v1/model-configs/${modelId}`),
    onSuccess: async () => {
      try {
        await qc.invalidateQueries({ queryKey: ['model-configs'] });
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.MODELS });
      } catch (err) {
        console.error(err);
      }
    },
  });

  const test = useMutation({
    mutationFn: () => client.http.post(`v1/model-configs/${modelId}/test`).json<ITestResult>(),
  });

  const testImage = useMutation({
    mutationFn: (input: { imageUrl: string; prompt: string }) =>
      client.http
        .post(`v1/model-configs/${modelId}/test-image`, { json: input })
        .json<ITestResult>(),
  });

  if (model.isLoading) return <Spinner />;
  if (model.error)
    return (
      <div className="alert alert--danger">Failed to load: {(model.error as Error).message}</div>
    );
  if (!model.data) return <EmptyState title="Not found" />;

  return (
    <>
      <PageHeader
        title={model.data.name}
        actions={
          <>
            <BackButton to={routes.MODELS} label="Models" />
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => test.mutate()}
              disabled={test.isPending}
            >
              {test.isPending ? 'Testing…' : 'Test'}
            </button>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => setTestImageOpen(true)}
            >
              Test image
            </button>
            <button
              type="button"
              className="btn btn--destructive btn--sm"
              onClick={() => {
                if (confirm(`Delete "${model.data?.name}"? Agents using it will fail to run.`))
                  remove.mutate();
              }}
              disabled={remove.isPending}
            >
              Delete
            </button>
          </>
        }
      />
      <div className="card" style={{ maxWidth: 720 }}>
        <div className="card-body">
          <form
            style={stack3}
            onSubmit={(e) => {
              e.preventDefault();
              update.mutate();
            }}
          >
            <div style={grid2}>
              <label className="field">
                <span className="field-label">Name</span>
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={true}
                />
              </label>
              <label className="field">
                <span className="field-label">Provider</span>
                <select
                  className="select-native"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                >
                  <option value="OPENAI">OPENAI</option>
                  <option value="ANTHROPIC">ANTHROPIC</option>
                  <option value="AZURE_OPENAI">AZURE_OPENAI</option>
                  <option value="BEDROCK">BEDROCK</option>
                  <option value="VERTEX">VERTEX</option>
                  <option value="OLLAMA">OLLAMA</option>
                  <option value="OPENAI_COMPATIBLE">OPENAI_COMPATIBLE</option>
                </select>
              </label>
            </div>
            <div style={grid2}>
              <label className="field">
                <span className="field-label">Model ID</span>
                <input
                  className="input"
                  value={modelIdField}
                  onChange={(e) => setModelIdField(e.target.value)}
                  placeholder="gpt-5 / claude-sonnet-4-6 / …"
                  required={true}
                />
              </label>
              <label className="field">
                <span className="field-label">Endpoint (optional)</span>
                <input
                  className="input"
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.target.value)}
                  placeholder="https://…"
                />
              </label>
            </div>
            <label className="field">
              <span className="field-label">API key</span>
              <input
                type="password"
                className="input"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter new key to rotate…"
                autoComplete="new-password"
              />
              <span className="field-help">
                Encrypted at rest. Leave blank to keep the existing key (if set).
              </span>
            </label>
            <fieldset style={{ border: 'none', padding: 0, margin: 0, ...stack3 }}>
              <legend className="field-label" style={{ padding: 0 }}>
                Generation parameters (optional)
              </legend>
              <div style={grid2}>
                <label className="field">
                  <span className="field-label">Temperature</span>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    className="input"
                    value={params.temperature}
                    onChange={(e) => setParam('temperature', e.target.value)}
                    placeholder="model default"
                  />
                </label>
                <label className="field">
                  <span className="field-label">Max output tokens</span>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    max="200000"
                    className="input"
                    value={params.maxOutputTokens}
                    onChange={(e) => setParam('maxOutputTokens', e.target.value)}
                    placeholder="model default"
                  />
                </label>
              </div>
              <label className="field">
                <span className="field-label">Top P</span>
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  max="1"
                  className="input"
                  value={params.topP}
                  onChange={(e) => setParam('topP', e.target.value)}
                  placeholder="model default"
                />
              </label>
              <span className="field-help">
                Applied to every call this config drives. Leave blank to use the provider default.
                Anthropic ignores temperature/top-P while extended thinking is active, and caps
                temperature at 1 (values above 1 are for OpenAI-compatible endpoints only).
              </span>
            </fieldset>
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <button type="submit" className="btn btn--primary" disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save'}
              </button>
              {update.error ? (
                <span
                  className="t-xs"
                  style={{ color: 'var(--status-danger)', alignSelf: 'center' }}
                >
                  {(update.error as Error).message}
                </span>
              ) : null}
            </div>
          </form>
        </div>
      </div>
      {test.data && (
        <div
          className={`alert alert--${
            test.data.success
              ? 'success'
              : test.data.failure?.kind === 'billing' || test.data.failure?.kind === 'rate_limit'
                ? 'warning'
                : 'danger'
          }`}
          style={{ marginTop: 'var(--sp-3)', maxWidth: 720 }}
        >
          <strong>{test.data.success ? '✓ Test passed' : '✗ Test failed'}</strong>
          <div className="t-sm" style={{ marginTop: 'var(--sp-1)' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 'var(--sp-2)',
                marginBottom: 'var(--sp-2)',
              }}
            >
              {typeof test.data.timeToFirstTokenMs === 'number' && (
                <div>
                  <span className="t-xs t-muted">Time to first token</span>
                  <div className="t-mono-label">{test.data.timeToFirstTokenMs}ms</div>
                </div>
              )}
              {typeof test.data.tokensPerSecond === 'number' && (
                <div>
                  <span className="t-xs t-muted">Tokens/second</span>
                  <div className="t-mono-label">{test.data.tokensPerSecond}</div>
                </div>
              )}
            </div>
            {test.data.totalTokens !== undefined && (
              <div className="t-xs t-muted" style={{ marginBottom: 'var(--sp-1)' }}>
                Total tokens: {test.data.totalTokens}
              </div>
            )}
            {test.data.preview && (
              <div style={{ marginTop: 'var(--sp-2)' }}>
                {test.data.reasoning && (
                  <div
                    style={{
                      marginBottom: 'var(--sp-2)',
                      padding: 'var(--sp-2)',
                      background: 'var(--surface-2)',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  >
                    <div className="t-xs t-muted" style={{ marginBottom: 'var(--sp-1)' }}>
                      Reasoning:
                    </div>
                    <div
                      style={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {test.data.reasoning}
                    </div>
                  </div>
                )}
                <div
                  style={{
                    padding: 'var(--sp-2)',
                    background: 'var(--surface-1)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  <div className="t-xs t-muted" style={{ marginBottom: 'var(--sp-1)' }}>
                    Haiku:
                  </div>
                  <div
                    style={{
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      fontSize: 'var(--fs-sm)',
                    }}
                  >
                    {test.data.preview.replace(
                      test.data.reasoning
                        ? `**Reasoning:**\n${test.data.reasoning}\n\n**Haiku:**\n`
                        : '',
                      '',
                    )}
                  </div>
                </div>
              </div>
            )}
            {test.data.error && (
              <ModelTestFailureAlert error={test.data.error} failure={test.data.failure} />
            )}
          </div>
        </div>
      )}
      {test.error && (
        <div className="alert alert--danger" style={{ marginTop: 'var(--sp-3)', maxWidth: 720 }}>
          <strong>Test failed</strong>
          <div className="t-sm" style={{ marginTop: 'var(--sp-1)' }}>
            {(test.error as Error).message}
          </div>
        </div>
      )}
      {testImage.isPending && (
        <div className="alert" style={{ marginTop: 'var(--sp-3)', maxWidth: 720 }}>
          <strong>Testing image…</strong>
        </div>
      )}
      {testImage.data && (
        <div
          className={`alert alert--${
            testImage.data.success
              ? 'success'
              : testImage.data.failure?.kind === 'billing' ||
                  testImage.data.failure?.kind === 'rate_limit'
                ? 'warning'
                : 'danger'
          }`}
          style={{ marginTop: 'var(--sp-3)', maxWidth: 720 }}
        >
          <strong>{testImage.data.success ? '✓ Image test passed' : '✗ Image test failed'}</strong>
          <div className="t-sm" style={{ marginTop: 'var(--sp-1)' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 'var(--sp-2)',
                marginBottom: 'var(--sp-2)',
              }}
            >
              {typeof testImage.data.timeToFirstTokenMs === 'number' && (
                <div>
                  <span className="t-xs t-muted">Time to first token</span>
                  <div className="t-mono-label">{testImage.data.timeToFirstTokenMs}ms</div>
                </div>
              )}
              {typeof testImage.data.tokensPerSecond === 'number' && (
                <div>
                  <span className="t-xs t-muted">Tokens/second</span>
                  <div className="t-mono-label">{testImage.data.tokensPerSecond}</div>
                </div>
              )}
            </div>
            {testImage.data.totalTokens !== undefined && (
              <div className="t-xs t-muted" style={{ marginBottom: 'var(--sp-1)' }}>
                Total tokens: {testImage.data.totalTokens}
              </div>
            )}
            {testImage.data.preview && (
              <div
                style={{
                  marginTop: 'var(--sp-2)',
                  padding: 'var(--sp-2)',
                  background: 'var(--surface-1)',
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  fontSize: 'var(--fs-sm)',
                }}
              >
                {testImage.data.preview}
              </div>
            )}
            {testImage.data.error && (
              <ModelTestFailureAlert
                error={testImage.data.error}
                failure={testImage.data.failure}
              />
            )}
          </div>
        </div>
      )}
      {testImage.error && (
        <div className="alert alert--danger" style={{ marginTop: 'var(--sp-3)', maxWidth: 720 }}>
          <strong>Image test failed</strong>
          <div className="t-sm" style={{ marginTop: 'var(--sp-1)' }}>
            {(testImage.error as Error).message}
          </div>
        </div>
      )}
      <TestImageModal
        open={testImageOpen}
        onClose={() => setTestImageOpen(false)}
        isPending={testImage.isPending}
        onSubmit={(input) => testImage.mutate(input)}
      />
    </>
  );
}

function ModelTestFailureAlert({ error, failure }: { error: string; failure?: ProviderFailure }) {
  const resolved = failure ?? providerFailureFromUnknown(error);
  if (!resolved) {
    return (
      <div style={{ marginTop: 'var(--sp-1)', color: 'var(--status-danger)' }}>Error: {error}</div>
    );
  }
  const accent =
    resolved.kind === 'billing'
      ? 'var(--status-warning, #c4882a)'
      : resolved.kind === 'rate_limit'
        ? 'var(--status-info, #3b82f6)'
        : 'var(--status-danger)';
  return (
    <div
      style={{
        marginTop: 'var(--sp-2)',
        padding: 'var(--sp-2)',
        borderRadius: 'var(--radius-sm)',
        border: `1px solid color-mix(in oklab, ${accent} 45%, transparent)`,
        background: `color-mix(in oklab, ${accent} 10%, transparent)`,
      }}
    >
      <div style={{ fontWeight: 600, color: accent }}>{resolved.title}</div>
      <div
        className="t-sm"
        style={{ marginTop: 4, color: 'var(--text-secondary)', lineHeight: 1.5 }}
      >
        {resolved.remediation}
      </div>
      <details style={{ marginTop: 8 }}>
        <summary className="t-xs t-muted" style={{ cursor: 'pointer' }}>
          Technical details
        </summary>
        <pre
          className="t-mono"
          style={{
            marginTop: 6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: 11,
            color: 'var(--text-secondary)',
          }}
        >
          {error}
        </pre>
      </details>
    </div>
  );
}
